import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { XIcon, SearchIcon, UsersIcon, UserMinusIcon } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Friend, removeFriend } from "../../lib/supabase";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface SearchFriendsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  friends: Friend[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

export const SearchFriendsDialog: React.FC<SearchFriendsDialogProps> = ({
  isOpen,
  onClose,
  friends,
  isLoading = false,
  onRefresh,
}) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [isMounted, setIsMounted] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [targetFriend, setTargetFriend] = useState<Friend | null>(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
    }
  }, [isOpen]);

  const filteredFriends = useMemo(() => {
    if (!searchQuery.trim()) {
      return friends;
    }

    const query = searchQuery.toLowerCase();

    return friends.filter(friend => {
      const name = friend.friend_profiles?.name?.toLowerCase() || "";
      const school = friend.friend_profiles?.asal_sekolah?.toLowerCase() || "";
      const role = friend.friend_profiles?.role?.toLowerCase() || "";

      return (
        name.includes(query) || school.includes(query) || role.includes(query)
      );
    });
  }, [friends, searchQuery]);

  const getAvatarUrl = (friend: Friend) => {
    if (friend.friend_profiles?.avatar_url) {
      return friend.friend_profiles.avatar_url;
    }

    return "/avatars/ava1.png";
  };

  const handleClose = () => {
    setSearchQuery("");
    onClose();
  };

  const handleRefreshClick = () => {
    setSearchQuery("");
    if (onRefresh) {
      onRefresh();
    }
  };

  if (!isMounted || !isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-3xl p-6 max-w-md w-full relative shadow-xl transform transition-all duration-300 scale-100 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] rounded-lg flex items-center justify-center">
              <SearchIcon className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-[#373e46]">
              Search Friends
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close search friends dialog"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search Input */}
        <div className="relative mb-4">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder="Cari berdasarkan nama, sekolah, atau peran"
            className="w-full pl-10 h-12 rounded-xl border-2 border-[#e3e6eb] focus:border-[#6b2cfe] transition-colors"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto space-y-3 min-h-0">
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(index => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 animate-pulse"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredFriends.length > 0 ? (
            filteredFriends.map(friend => {
              const isRemoving = removingId === friend.friend_id;
              const handleAskRemove = () => {
                setTargetFriend(friend);
                setShowConfirm(true);
              };
              return (
                <div
                  key={friend.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                >
                  <Avatar className="w-12 h-12 flex-shrink-0">
                    <AvatarFallback>
                      <img
                        className="w-full h-full object-cover"
                        alt={`${friend.friend_profiles?.name || "Friend"} Avatar`}
                        src={getAvatarUrl(friend)}
                      />
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-[#373e46] text-sm truncate">
                      {friend.friend_profiles?.name || "Teman Tanpa Nama"}
                    </h4>
                    <p className="text-xs text-gray-500 truncate">
                      {friend.friend_profiles?.asal_sekolah ||
                        "Sekolah tidak diketahui"}
                      {friend.friend_profiles?.role &&
                        ` • ${friend.friend_profiles.role}`}
                    </p>
                  </div>

                  <Button
                    onClick={handleAskRemove}
                    disabled={isRemoving}
                    size="sm"
                    variant="outline"
                    className={`px-2 py-1 rounded-lg font-medium transition-all duration-300 text-xs h-7 flex items-center gap-1 border-red-300 text-red-600 hover:bg-red-50 ${
                      isRemoving ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {isRemoving ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                    ) : (
                      <UserMinusIcon className="w-3 h-3" />
                    )}
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-[#373e46] mb-2">
                {searchQuery ? "Teman tidak ditemukan" : "Belum ada teman"}
              </h4>
              <p className="text-gray-500 text-sm">
                {searchQuery
                  ? `Tidak ada teman yang cocok dengan "${searchQuery}"`
                  : "Tambahkan teman untuk mulai berkolaborasi"}
              </p>
              {onRefresh && (
                <Button
                  onClick={handleRefreshClick}
                  className="mt-4 bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg text-sm"
                >
                  Refresh
                </Button>
              )}
            </div>
          )}
        </div>
        <ConfirmationDialog
          isOpen={showConfirm}
          onClose={() => {
            setShowConfirm(false);
            setTargetFriend(null);
          }}
          onConfirm={async () => {
            if (!user?.id || !targetFriend || removingId) return;
            setRemovingId(targetFriend.friend_id);
            try {
              const res = await removeFriend(user.id, targetFriend.friend_id);
              if (res.success) {
                toast.success(
                  `${targetFriend.friend_profiles?.name || "Teman"} telah dihapus dari daftar teman`
                );
                if (onRefresh) onRefresh();
              } else {
                toast.error(res.error || "Gagal menghapus teman");
              }
            } catch (e) {
              console.error("Error removing friend:", e);
              toast.error("Terjadi kesalahan saat menghapus teman");
            } finally {
              setRemovingId(null);
              setShowConfirm(false);
              setTargetFriend(null);
            }
          }}
          title="Hapus Teman"
          message={`Apakah Anda yakin ingin menghapus ${targetFriend?.friend_profiles?.name || "teman ini"} dari daftar teman? Tindakan ini tidak dapat dibatalkan.`}
          confirmText="Hapus"
          cancelText="Batal"
          variant="danger"
        />
      </div>
    </div>,
    document.body
  );
};
